import { deactivateProduct, deleteProduct } from "../../../services";
import { ProductResponse } from "../../../types/product.type";
import { numberWithCommas } from "../../../utils";
import CatalogueDeleteDialog from "../shared/CatalogueDeleteDialog";
import { useCatalogueDelete } from "../shared/useCatalogueDelete";

/** A running sales round keeps selling until the day is closed, so that is when to delete. */
const DELETE_HINT = "ควรลบหลังปิดวัน";

const buildConsequences = (product: ProductResponse): string[] => [
  `ลบออกจากทุกสาขา (${numberWithCommas(product.branch_count)} สาขา)`,
  `ราคาของสินค้านี้ใน ${numberWithCommas(
    product.pricebookItems.length,
  )} สมุดราคาถูกลบด้วย`,
  "sale จะเลือกขายสินค้านี้ไม่ได้ทันที รวมถึงรอบที่กำลังวิ่งอยู่",
  "บิลและรายงานที่ออกไปแล้วไม่เปลี่ยน",
  "รายงานที่สร้างหลังจากนี้จะไม่มีสินค้านี้",
  'กู้คืนไม่ได้ — ถ้าแค่หยุดขายชั่วคราวให้ใช้ "ปิด"',
];

interface ProductDeleteDialogProps {
  /**
   * The product being asked about, as GET /products/:uuid returns it: only that read carries
   * the pricebooks the delete takes the product's prices out of. Null keeps the dialog closed.
   */
  product: ProductResponse | null;
  onClose: () => void;
  onDeleted: () => void;
  /** The product was switched off instead of deleted. */
  onDeactivated: () => void;
}

/**
 * Type-to-confirm delete for one product, shared by the list row and the edit drawer so both
 * spell out the same consequences.
 */
export default function ProductDeleteDialog({
  product,
  onClose,
  onDeleted,
  onDeactivated,
}: ProductDeleteDialogProps) {
  const deleteRecord = async () => {
    if (!product) return;
    await deleteProduct(product.uuid);
    onDeleted();
  };

  const deactivateRecord = async () => {
    if (!product) return;
    await deactivateProduct(product.uuid);
    onDeactivated();
  };

  const { deleting, deactivateAction, handleClose, handleDelete } =
    useCatalogueDelete({
      deleteRecord,
      deactivateRecord,
      deactivateLabel: "ปิดสินค้าแทน",
      canDeactivate: product !== null && product.status !== "disable",
      onClose,
    });

  return (
    <CatalogueDeleteDialog
      open={Boolean(product)}
      title={`ลบสินค้า ${product?.code ?? ""} ${product?.name ?? ""}`}
      code={product?.code ?? ""}
      consequences={product ? buildConsequences(product) : []}
      hint={DELETE_HINT}
      deleting={deleting}
      deactivateAction={deactivateAction}
      onClose={handleClose}
      onDelete={handleDelete}
    />
  );
}
